import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Sparkles, MapPin } from 'lucide-react'
import { useCreateTrip } from './useTrips'
import { planTrip, applyPlan } from '../../shared/api/ai'
import type { AiPlanPreview } from '../../shared/api/ai.http'
import { pointKeys } from '../timeline/usePoints'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { POINT_TYPE_META } from '../../shared/lib/pointMeta'
import type { Trip } from '../../shared/api/types'

export function CreateTripAi() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const createTrip = useCreateTrip()
  const [title, setTitle] = useState('')
  const [destination, setDestination] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [prompt, setPrompt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [trip, setTrip] = useState<Trip | null>(null)
  const [preview, setPreview] = useState<AiPlanPreview | null>(null)
  const [planning, setPlanning] = useState(false)

  const busy = createTrip.isPending || planning

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!title.trim()) {
      setError(t('ai_create.name_required'))
      return
    }
    if (startDate && endDate && endDate < startDate) {
      setError(t('ai_create.dates_invalid'))
      return
    }
    setPlanning(true)
    try {
      // The plan endpoint needs a trip id, so create the trip first, then plan.
      const created =
        trip ??
        (await createTrip.mutateAsync({
          title: title.trim(),
          destination: destination.trim(),
          start_date: startDate || null,
          end_date: endDate || null,
          time_zone_id: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC',
        }))
      setTrip(created)
      const generated = await planTrip(
        created.id,
        prompt.trim() || destination.trim() || title.trim(),
      )
      setPreview(generated)
    } catch {
      setError(t('ai_create.plan_error'))
    } finally {
      setPlanning(false)
    }
  }

  if (preview) {
    return (
      <div className="mx-auto max-w-xl">
        <h1 className="font-display text-2xl font-bold text-st-text">{t('ai_create.draft_title')}</h1>
        <p className="mt-1 text-sm text-st-muted">
          {t('ai_create.draft_intro', { count: preview.points.length, title: trip?.title })}
        </p>

        <ul className="mt-6 space-y-2">
          {preview.points.map((p, i) => {
            const meta = POINT_TYPE_META[p.type as keyof typeof POINT_TYPE_META]
            const Icon = meta?.Icon ?? MapPin
            return (
              <li
                key={`${p.title}-${i}`}
                className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface px-3 py-2.5"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-st-sm bg-st-surface-2 text-st-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-st-text">{p.title}</span>
                  <span className="text-xs text-st-muted">{t('ai_create.day', { number: p.day_index + 1 })}</span>
                  {p.reason && <span className="block text-xs text-st-muted">{p.reason}</span>}
                </span>
              </li>
            )
          })}
        </ul>

        <div className="mt-6 flex gap-3">
          <Button variant="accent" onClick={async () => { try {
            await applyPlan(trip!.id, preview); await qc.invalidateQueries({ queryKey: pointKeys.byTrip(trip!.id) })
            navigate(`/trips/${trip!.id}/places`)
          } catch (err) { setError(err instanceof Error ? err.message : 'Could not add route') } }}>
            {t('ai_create.add')}
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/trips/${trip!.id}/places`)}>
            {t('ai_create.skip')}
          </Button>
          {error && <p role="alert" className="text-st-danger">{error}</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl">
      <Link
        to="/trips/new"
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> {t('ai_create.back')}
      </Link>

      <h1 className="font-display text-2xl font-bold text-st-text">{t('ai_create.title')}</h1>
      <p className="mt-1 text-sm text-st-muted">
        {t('ai_create.intro')}
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <Field label={t('ai_create.trip_title')} required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('ai_create.trip_hint')}
            className="input"
            autoFocus
          />
        </Field>

        <Field label={t('ai_create.destination')}>
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={t('ai_create.destination_hint')}
            className="input"
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('ai_create.start')}><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input" /></Field>
          <Field label={t('ai_create.end')}><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input" /></Field>
        </div>

        <Field label={t('ai_create.describe')}>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('ai_create.describe_hint')}
            className="input min-h-24 py-2"
            rows={4}
          />
        </Field>

        {error && <p className="text-sm text-st-danger">{error}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="submit" variant="accent" disabled={busy}>
            {busy ? (
              <Spinner label={t('ai_create.generating')} />
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> {t('ai_create.generate')}
              </>
            )}
          </Button>
          <Link to="/trips/new">
            <Button type="button" variant="ghost">
              {t('ai_create.cancel')}
            </Button>
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          min-height: 2.75rem;
          border-radius: var(--radius-st-md);
          border: 1px solid var(--color-st-line);
          background: var(--color-st-surface);
          padding: 0 0.875rem;
          color: var(--color-st-text);
          font-size: 0.95rem;
        }
        .input:focus { outline: 2px solid var(--color-st-info); outline-offset: 1px; }
      `}</style>
    </div>
  )
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-st-text">
        {label}
        {required && <span className="text-st-danger"> *</span>}
      </span>
      {children}
    </label>
  )
}
